import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Check, Loader2, XCircle } from "lucide-react";
import { fetchEnrollment } from "@/lib/enrollment/api";

const MAX_ATTEMPTS = 10; // ~20 seconds total at 2s interval

export default function PaymentReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [message, setMessage] = useState("Confirming your payment…");

  useEffect(() => {
    // Stripe only redirects users to success_url after a completed payment,
    // so arriving here means payment succeeded. We poll our DB to confirm
    // the Stripe webhook fired and marked the enrollment as paid.
    let cancelled = false;
    let storedEmail = "";
    try {
      storedEmail = localStorage.getItem("prsvr.enrollment.email") || "";
    } catch {
      // ignore
    }

    if (!storedEmail) {
      setStatus("paid");
      setMessage("Payment received. Redirecting…");
      setTimeout(() => navigate("/?paid=1", { replace: true }), 1200);
      return;
    }

    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const doc = await fetchEnrollment(storedEmail);
        if (doc?.payment_status === "paid") {
          setStatus("paid");
          setMessage("Payment confirmed. Redirecting…");
          setTimeout(() => navigate("/?paid=1", { replace: true }), 1100);
          return;
        }
      } catch {
        // ignore transient errors
      }
      if (attempts < MAX_ATTEMPTS) {
        setTimeout(poll, 2000);
      } else {
        // Webhook is delayed but payment was successful (we're on the success URL).
        // Show confirmation; the webhook will catch up.
        if (!cancelled) {
          setStatus("paid");
          setMessage("Payment received. The dashboard will update shortly.");
          setTimeout(() => navigate("/?paid=1", { replace: true }), 1800);
        }
      }
    };
    poll();

    return () => {
      cancelled = true;
    };
  }, [sessionId, navigate]);

  return (
    <div className="min-h-screen bg-cream-grain flex items-center justify-center px-5" data-testid="payment-return">
      <div className="bg-white border border-[#E5E0D8] rounded-sm p-10 max-w-md w-full text-center">
        {status === "checking" && (
          <Loader2 size={36} className="text-[#1B3B2B] animate-spin mx-auto" />
        )}
        {status === "paid" && (
          <div className="inline-flex w-14 h-14 items-center justify-center rounded-full bg-[#1B3B2B] text-white">
            <Check size={28} />
          </div>
        )}
        {status === "error" && <XCircle size={36} className="text-[#991B1B] mx-auto" />}
        <h1 className="font-serif-display text-3xl text-[#1B3B2B] mt-5">
          {status === "paid" ? "Setup & Gear Fee Received" : "Confirming Payment"}
        </h1>
        <p className="text-[#4B5563] mt-3" data-testid="payment-return-message">{message}</p>
        {status === "error" && (
          <button
            onClick={() => navigate("/", { replace: true })}
            className="mt-6 inline-flex items-center justify-center bg-[#1B3B2B] text-white hover:bg-[#132A13] transition-colors rounded-sm px-6 py-3 font-semibold uppercase tracking-wider text-xs"
            data-testid="payment-return-back"
          >
            Back to Enrollment
          </button>
        )}
      </div>
    </div>
  );
}
